import { ReactComponent as GeneralIcon } from '@ubeya/shared-web/assets/settingAccount.svg';
import { ReactComponent as EmployeeIcon } from '@ubeya/shared-web/assets/id.svg';
import { ReactComponent as SchedulingIcon } from '@ubeya/shared-web/assets/calendar.svg';
import { ReactComponent as PayrollIcon } from '@ubeya/shared-web/assets/payroll-dollar.svg';
import { ReactComponent as RulesIcon } from '@ubeya/shared-web/assets/configure.svg';
import { ReactComponent as HelpDeskIcon } from '@ubeya/shared-web/assets/module-help-desk.svg';
import { ReactComponent as TaskFieldsIcon } from '@ubeya/shared-web/assets/field-number.svg';
import { ReactComponent as SupplierIcon } from '@ubeya/shared-web/assets/module-suppliers.svg';
import { ReactComponent as ClientIcon } from '@ubeya/shared-web/assets/module-crm.svg';
import { ReactComponent as CompanyIcon } from '@ubeya/shared-web/assets/module-directory.svg';
import { ReactComponent as AdminIcon } from '@ubeya/shared-web/assets/admin-reporter.svg';
import { ReactComponent as KioskIcon } from '@ubeya/shared-web/assets/qr-code.svg';
import privileges from '@ubeya/shared-web/privileges';

export const SCOPE = { GLOBAL: 'global', BRANCH: 'branch' };

export const SEARCH_FOCUS_PARAM = 'settingsFocus';

// When a child control is conditionally rendered behind a parent toggle, this
// carries the parent's data-setting-field so the deep-link can land on the
// (always-visible) parent when the child itself is not mounted.
export const SEARCH_FOCUS_PARENT_PARAM = 'settingsFocusParent';

const accountRoute = (tabKey, subTabKey) => `/:accountId/settings/account/${tabKey}${subTabKey ? `/${subTabKey}` : ''}`;

const branchRoute = (tabKey, subTabKey) =>
  `/:accountId/settings/account/branch/:branchId/${tabKey}${subTabKey ? `/${subTabKey}` : ''}`;

export const SETTINGS_TABS = {
  general: { icon: GeneralIcon, scope: SCOPE.GLOBAL, isBranch: false },
  'employee-profile': { icon: EmployeeIcon, scope: SCOPE.GLOBAL, isBranch: false },
  'scheduling-timesheet': { icon: SchedulingIcon, scope: SCOPE.BRANCH, isBranch: true },
  payroll: { icon: PayrollIcon, scope: SCOPE.GLOBAL, isBranch: false },
  'rules-engine': { icon: RulesIcon, scope: SCOPE.GLOBAL, isBranch: false },
  'help-desk': { icon: HelpDeskIcon, scope: SCOPE.BRANCH, isBranch: true },
  'timesheet-fields': { icon: TaskFieldsIcon, scope: SCOPE.GLOBAL, isBranch: false },
  'supplier-settings': { icon: SupplierIcon, scope: SCOPE.GLOBAL, isBranch: false },
  'client-settings': { icon: ClientIcon, scope: SCOPE.GLOBAL, isBranch: false },
  'company-profile': { icon: CompanyIcon, scope: SCOPE.GLOBAL, isBranch: false },
  'admin-management': { icon: AdminIcon, scope: SCOPE.GLOBAL, isBranch: false },
  kiosk: { icon: KioskIcon, scope: SCOPE.GLOBAL, isBranch: false }
};

/**
 * Each entry describes one navigable setting (or a table/CRUD sub-page indexed
 * at page granularity). The palette matches against the translated label,
 * description, section, tab, sub-page and keyword list.
 *
 * - labelKey / sectionKey / subTabKey are i18n keys resolved through `t`.
 * - searchLabelKey: optional plain-text override for labelKey. Use it when the
 *   on-screen label is authored for <Trans> (numbered tags around an inline input,
 *   {{var}} placeholders) — that markup is not strippable across locales, so those
 *   controls get their own plain key instead. The palette shows searchLabelKey when
 *   present and falls back to labelKey otherwise.
 * - scope: 'global' shows a live value in the row; 'branch' shows a neutral
 *   "Per branch" indicator (value depends on the selected branch).
 * - field + fieldType + valueBase let the palette read a live value for global
 *   toggles/selects and let the deep-link anchor the control (data-setting-field).
 * - parentField: the data-setting-field of a parent toggle that must be ON for this
 *   control to render. When the child's own anchor is absent (parent off), the
 *   deep-link falls back to scroll+highlight the parent instead. Optional. Only for
 *   parents rendered on the same page — the deep-link searches the active settings
 *   page only, so a gate that lives on another tab must use a visibility gate below.
 * - requiresFlatWage: the control only renders while payrollConfig.flatIsActive is ON,
 *   and its gate (the Payroll › General "flat wage" toggle) lives on another tab, so
 *   there is nothing on-page to fall back to. Drop it from search while flat is off;
 *   the Payroll toggle itself stays searchable so the user can turn it on.
 * - privilege / requiresUbeyaAdmin / hideOnPartialSettings / countryCode: finer-than-tab
 *   gating (see hooks/useVisibleSettingsTabs.isControlVisible). A control is removed from
 *   search results whenever the current user cannot render it, mirroring the exact gate
 *   the component uses (a specific privilege, Ubeya-admin-only, the branch-admin/partial
 *   reduced view, or a country-restricted section). Optional; entries without them are
 *   gated only at tab/sub-page level as before.
 */
const raw = [
  // ── GENERAL (global, single page) ──────────────────────────────────────────
  // Account info
  {
    id: 'general.accountInfo.accountName',
    tabKey: 'general',
    sectionKey: 'accountInfo',
    labelKey: 'accountName',
    field: 'name',
    keywords: ['account name', 'company name', 'organization', 'business name']
  },
  {
    id: 'general.accountInfo.replyToEmail',
    tabKey: 'general',
    sectionKey: 'accountInfo',
    labelKey: 'replyToEmailSettingsTitle',
    field: 'replyToEmail',
    keywords: ['email', 'company email', 'reply to', 'contact email', 'email address']
  },
  {
    id: 'general.accountInfo.industry',
    tabKey: 'general',
    sectionKey: 'accountInfo',
    labelKey: 'industry',
    field: 'industryId',
    fieldType: 'select',
    keywords: ['sector', 'business type', 'vertical']
  },
  // Localization
  {
    id: 'general.localization.country',
    tabKey: 'general',
    sectionKey: 'localization',
    labelKey: 'country',
    field: 'countryId',
    fieldType: 'select',
    keywords: ['region', 'nation']
  },
  {
    id: 'general.localization.language',
    tabKey: 'general',
    sectionKey: 'localization',
    labelKey: 'language',
    field: 'languageId',
    fieldType: 'select',
    keywords: ['locale', 'translation']
  },
  {
    id: 'general.localization.currency',
    tabKey: 'general',
    sectionKey: 'localization',
    labelKey: 'currency',
    field: 'currencyId',
    fieldType: 'select',
    keywords: ['money', 'pay']
  },
  {
    id: 'general.localization.hourFormat',
    tabKey: 'general',
    sectionKey: 'localization',
    labelKey: 'hourFormat',
    field: 'hourFormat',
    fieldType: 'select',
    keywords: ['time', '12', '24']
  },
  {
    id: 'general.localization.dateFormat',
    tabKey: 'general',
    sectionKey: 'localization',
    labelKey: 'dateFormat',
    field: 'dateFormat',
    fieldType: 'select',
    keywords: ['region']
  },
  {
    id: 'general.localization.firstDayOfWeek',
    tabKey: 'general',
    sectionKey: 'localization',
    labelKey: 'firstDayOfWeek',
    field: 'firstDayOfWeek',
    fieldType: 'select',
    keywords: ['week start']
  },
  {
    id: 'general.localization.distanceUnit',
    tabKey: 'general',
    sectionKey: 'localization',
    labelKey: 'distanceUnit',
    field: 'distanceUnit',
    fieldType: 'select',
    keywords: ['km', 'miles', 'kilometers']
  },
  // Other
  {
    id: 'general.other.autoLogout',
    tabKey: 'general',
    sectionKey: 'other',
    labelKey: 'applyAutoLogoutToggle',
    field: 'config.inactivityInterval',
    hideOnPartialSettings: true,
    keywords: ['logout', 'idle', 'session', 'timeout', 'inactivity']
  },
  {
    id: 'general.other.autoLogoutPeriod',
    tabKey: 'general',
    sectionKey: 'other',
    labelKey: 'autoLogoutPeriod',
    searchLabelKey: 'settingsSearchAutoLogoutPeriod',
    field: 'config.inactivityIntervalPeriod',
    parentField: 'config.inactivityInterval',
    fieldType: 'select',
    hideOnPartialSettings: true,
    keywords: ['logout', 'idle', 'session', 'timeout', 'inactivity', 'minutes', 'hours', 'days']
  },
  {
    id: 'general.other.photoVerification',
    tabKey: 'general',
    sectionKey: 'other',
    labelKey: 'showPhotoVerificationStatus',
    field: 'config.isPhotoVerificationEnabled',
    fieldType: 'toggle',
    defaultOn: true,
    hideOnPartialSettings: true,
    keywords: ['photo', 'selfie', 'camera', 'id']
  },
  {
    id: 'general.other.blockOverlappingShifts',
    tabKey: 'general',
    sectionKey: 'other',
    labelKey: 'settingsBlockOverlappingShifts',
    field: 'config.blockOverlappingShiftsAcrossBranches',
    fieldType: 'toggle',
    hideOnPartialSettings: true,
    keywords: ['overlap', 'double booking']
  },
  {
    id: 'general.other.showLastNameFirst',
    tabKey: 'general',
    sectionKey: 'other',
    labelKey: 'settingsShowLastNameFirst',
    field: 'config.isShowLastNameFirst',
    fieldType: 'toggle',
    hideOnPartialSettings: true,
    keywords: ['name order', 'last name', 'first name', 'display name']
  },
  {
    id: 'general.other.birthdayNotification',
    tabKey: 'general',
    sectionKey: 'other',
    labelKey: 'enableAutomaticBirthdayNotification',
    field: 'config.birthdayMessage',
    keywords: ['birthday', 'notification', 'greeting']
  },
  {
    id: 'general.schedulingDefaults.unavailableByDefault',
    tabKey: 'general',
    sectionKey: 'schedulingDefaults',
    labelKey: 'unavailableByDefault',
    field: 'unavailableByDefault',
    fieldType: 'toggle',
    keywords: ['availability', 'general availability', 'weekly preference', 'not available', 'default']
  },
  {
    id: 'general.other.seniorityModel',
    tabKey: 'general',
    sectionKey: 'other',
    labelKey: 'enableSeniorityBooking',
    field: 'config.seniorityModel',
    fieldType: 'toggle',
    requiresUbeyaAdmin: true,
    hideOnPartialSettings: true,
    keywords: ['employee pools', 'pools', 'employee level', 'teams', 'seniority', 'tenure', 'ranking']
  },
  {
    id: 'general.other.invitationSMSTemplate',
    tabKey: 'general',
    sectionKey: 'other',
    labelKey: 'invitationSMSTemplate',
    field: 'defaultInvitationSMSTemplate',
    requiresUbeyaAdmin: true,
    keywords: ['sms', 'invitation', 'invite', 'download the app', 'text message', 'template']
  },

  // ── EMPLOYEE PROFILE (global, table/CRUD sub-pages) ─────────────────────────
  {
    id: 'employee-profile.fields',
    tabKey: 'employee-profile',
    subTabKey: 'fields',
    labelKey: 'fields',
    pageLevel: true,
    keywords: ['custom field', 'profile']
  },
  {
    id: 'employee-profile.fields.shareEmployeeId',
    tabKey: 'employee-profile',
    subTabKey: 'fields',
    labelKey: 'settingsShowEmployeeIdCrewApp',
    field: 'config.shareEmployeeIdWithEmployee',
    fieldType: 'toggle',
    keywords: ['employee id', 'show employee id', 'crew app', 'staff app', 'id number', 'employee number']
  },
  {
    id: 'employee-profile.positions',
    tabKey: 'employee-profile',
    subTabKey: 'positions',
    labelKey: 'positions',
    pageLevel: true,
    keywords: ['roles', 'job title']
  },
  {
    id: 'employee-profile.departments',
    tabKey: 'employee-profile',
    subTabKey: 'departments',
    labelKey: 'departments',
    pageLevel: true
  },
  {
    id: 'employee-profile.working-time-directive',
    tabKey: 'employee-profile',
    subTabKey: 'working-time-directive',
    labelKey: 'workingTimeDirective',
    pageLevel: true,
    keywords: ['wtd', 'compliance', 'hours']
  },
  {
    id: 'employee-profile.contracts',
    tabKey: 'employee-profile',
    subTabKey: 'contracts',
    labelKey: 'contracts',
    pageLevel: true,
    keywords: ['agreement', 'document'],
    // Route stops mounting once v2 CLM leave contracts are on (EmployeeProfile.js) — without
    // this, search still finds it and navigates to a dead path that falls through to Fields.
    hideWhenContractLeaveManagement: true
  },

  // ── SCHEDULING & TIMESHEET (per-branch) ─────────────────────────────────────
  // Scheduling › General
  {
    id: 'sched.scheduling.general.defaultSchedulingView',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'general',
    labelKey: 'defaultSchedulingView',
    field: 'defaultScheduleView',
    fieldType: 'select',
    keywords: ['calendar', 'weekly', 'monthly']
  },
  {
    id: 'sched.scheduling.general.defaultWeeklyView',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'general',
    labelKey: 'defaultWeeklyView',
    field: 'defaultWeeklyGroupByView',
    fieldType: 'select',
    parentField: 'defaultScheduleView',
    keywords: ['weekly', 'group by']
  },
  {
    id: 'sched.scheduling.general.defaultCalenderView',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'general',
    labelKey: 'defaultCalenderView',
    field: 'defaultMonthlyView',
    fieldType: 'select',
    parentField: 'defaultScheduleView',
    keywords: ['monthly', 'calendar']
  },
  {
    id: 'sched.scheduling.general.defaultEmployeeWorkingHoursPeriod',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'general',
    labelKey: 'defaultEmployeeWorkingHoursPeriod',
    field: 'defaultEmployeeWorkingHoursPeriod',
    fieldType: 'select',
    keywords: ['weekly', 'bi-weekly', 'monthly']
  },
  {
    id: 'sched.scheduling.general.requireClientPerJob',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'general',
    labelKey: 'requireClientPerJob',
    field: 'requireClientPerJob',
    fieldType: 'toggle'
  },
  {
    id: 'sched.scheduling.general.requireBookerPerJob',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'general',
    labelKey: 'requireBookerPerJob',
    field: 'requireBookerPerJob',
    fieldType: 'toggle'
  },
  {
    id: 'sched.scheduling.general.requireMainEventPerJob',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'general',
    labelKey: 'requireMainEventPerJob',
    field: 'requireMainEventPerJob',
    fieldType: 'toggle'
  },
  {
    id: 'sched.scheduling.general.isCostCenterRequired',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'general',
    labelKey: 'makeCostCenterRequiredOnEventCreation',
    field: 'isCostCenterRequired',
    fieldType: 'toggle',
    keywords: ['cost center', 'required', 'event']
  },
  {
    id: 'sched.scheduling.general.defaultBranchCostCenterId',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'general',
    labelKey: 'defaultCostCenter',
    field: 'defaultBranchCostCenterId',
    fieldType: 'select',
    parentField: 'isCostCenterRequired',
    keywords: ['cost center', 'default']
  },
  {
    id: 'sched.scheduling.general.showRejectedEmployees',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'general',
    labelKey: 'showEmployeesRejectionsInAvailableColumn',
    field: 'showRejectedEmployees',
    fieldType: 'toggle',
    keywords: ['rejections', 'available']
  },
  {
    id: 'sched.scheduling.general.enableOpeningEventChatGroups',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'general',
    labelKey: 'enableOpeningEventChatGroups',
    field: 'enableOpeningEventChatGroups',
    fieldType: 'toggle',
    keywords: ['chat', 'message', 'conversation']
  },
  {
    id: 'sched.scheduling.general.projectChat',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'general',
    labelKey: 'projectChat',
    field: 'projectChat',
    fieldType: 'toggle',
    parentField: 'enableOpeningEventChatGroups',
    keywords: ['chat', 'event', 'group']
  },
  {
    id: 'sched.scheduling.general.canViewAllSchedules',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'general',
    labelKey: 'canViewAllSchedules',
    field: 'usersCanViewAllSchedules',
    fieldType: 'toggle'
  },
  {
    id: 'sched.scheduling.general.pauseAvailabilityUponCompletion',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'general',
    labelKey: 'pauseAvailabilityUponCompletion',
    field: 'pauseAvailabilityUponCompletion',
    fieldType: 'toggle',
    keywords: ['availability']
  },
  {
    id: 'sched.scheduling.general.releaseEmployeesUponCompletion',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'general',
    labelKey: 'releaseEmployeesUponCompletion',
    field: 'releaseEmployeesUponCompletion',
    fieldType: 'toggle',
    parentField: 'pauseAvailabilityUponCompletion',
    keywords: ['release']
  },
  {
    id: 'sched.scheduling.general.allowQuickJob',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'general',
    labelKey: 'allowQuickJob',
    field: 'allowQuickJob',
    fieldType: 'toggle'
  },
  {
    id: 'sched.scheduling.general.positionRequired',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'general',
    labelKey: 'positionRequired',
    field: 'positionRequired',
    fieldType: 'toggle',
    keywords: ['position', 'role']
  },
  {
    id: 'sched.scheduling.general.socialsShareEnabled',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'general',
    labelKey: 'socialsShareEnabled',
    field: 'socialsShareEnabled',
    fieldType: 'toggle',
    keywords: ['social', 'share']
  },
  {
    id: 'sched.scheduling.general.allowAddLocationsFromEditShifts',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'general',
    labelKey: 'allowAddLocationsFromEditShifts',
    field: 'allowAddLocationsFromEditShifts',
    fieldType: 'toggle',
    keywords: ['location', 'shift']
  },
  {
    id: 'sched.scheduling.general.allowAddLocationsFromCreateEventOrder',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'general',
    labelKey: 'allowAddLocationsFromCreateEventOrder',
    field: 'allowAddLocationsFromCreateEventOrder',
    fieldType: 'toggle',
    keywords: ['location', 'event', 'order']
  },
  {
    id: 'sched.scheduling.general.isSendNotificationsBookerOnly',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'general',
    labelKey: 'sendNotificationsOnlyToEventBooker',
    field: 'isSendNotificationsBookerOnly',
    fieldType: 'toggle',
    keywords: ['notification', 'booker']
  },
  {
    id: 'sched.scheduling.general.applicationsByPositions',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'general',
    labelKey: 'publishApplicationAccordingToPositions',
    field: 'applicationsByPositions',
    fieldType: 'toggle',
    requiresUbeyaAdmin: true,
    keywords: ['application', 'position', 'publish']
  },
  {
    id: 'sched.scheduling.general.isRemoteWork',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'general',
    labelKey: 'isRemoteWork',
    field: 'isRemoteWork',
    fieldType: 'toggle',
    requiresUbeyaAdmin: true,
    keywords: ['remote', 'timezone', 'local time', 'shift times']
  },
  // Scheduling › Booking
  {
    id: 'sched.scheduling.booking.autoBook',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'booking',
    labelKey: 'autoBook',
    field: 'autoBook',
    fieldType: 'toggle',
    keywords: ['automatic']
  },
  {
    id: 'sched.scheduling.booking.autobookPreferredByClient',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'booking',
    labelKey: 'autobookPreferredByClient',
    field: 'autobookPreferredByClient',
    fieldType: 'toggle',
    parentField: 'autoBook',
    keywords: ['automatic', 'preferred', 'client']
  },
  {
    id: 'sched.scheduling.booking.allowOverbooking',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'booking',
    labelKey: 'allowOverbooking',
    field: 'allowOverbooking',
    fieldType: 'toggle'
  },
  {
    id: 'sched.scheduling.booking.allowUnbookNotes',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'booking',
    labelKey: 'allowUnbookNotes',
    field: 'allowUnbookNotes',
    fieldType: 'toggle',
    keywords: ['unbook', 'admin notes', 'internal notes', 'notes']
  },
  // Scheduling › Swap and release
  {
    id: 'sched.scheduling.swap.showReleaseShift',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'swapAndRelease',
    labelKey: 'showReleaseShiftOnStaffApp',
    field: 'showReleaseShift',
    fieldType: 'toggle',
    keywords: ['release', 'drop shift']
  },
  {
    id: 'sched.scheduling.swap.showSwapShift',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'swapAndRelease',
    labelKey: 'showSwapShiftOnStaffApp',
    field: 'showSwapShift',
    fieldType: 'toggle',
    keywords: ['swap', 'exchange']
  },
  {
    id: 'sched.scheduling.swap.autoSwapShift',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'swapAndRelease',
    labelKey: 'autoSwapShift',
    field: 'autoSwapShift',
    fieldType: 'toggle',
    keywords: ['swap', 'automatic']
  },
  {
    id: 'sched.scheduling.swap.hoursToRequestReleaseBeforeShiftStarts',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'swapAndRelease',
    labelKey: 'hoursToRequestReleaseBeforeShiftStarts',
    searchLabelKey: 'settingsSearchRequestReleaseBeforeShiftStart',
    field: 'hoursToRequestReleaseBeforeShiftStarts',
    parentField: 'showReleaseShift',
    keywords: [
      'release',
      'hours',
      'before shift',
      'deadline',
      'request release',
      'shift release',
      'employees can request release',
      'staff'
    ]
  },
  // Scheduling › App
  {
    id: 'sched.scheduling.app.defaultProjectGeneralComment',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'app',
    labelKey: 'defaultProjectGeneralComment',
    field: 'defaultProjectGeneralComment',
    keywords: ['default comment', 'general comment', 'message', 'template']
  },
  {
    id: 'sched.scheduling.app.defaultReleaseFromShift',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'app',
    labelKey: 'defaultReleaseFromShiftMessage',
    field: 'defaultReleaseFromShift',
    keywords: ['release', 'default message', 'template', 'text']
  },
  {
    id: 'sched.scheduling.app.isNotificationUponUnbook',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'app',
    labelKey: 'askForUnbookedFromShiftMessage',
    field: 'isNotificationUponUnbook',
    fieldType: 'toggle',
    keywords: ['unbook', 'notification', 'app']
  },
  {
    id: 'sched.scheduling.app.isShowOfferWage',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'app',
    labelKey: 'showOfferWageOnApp',
    field: 'isShowOfferWage',
    fieldType: 'toggle',
    keywords: ['wage', 'offer', 'app', 'pay']
  },
  {
    id: 'sched.scheduling.app.isShowBookingWage',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'app',
    labelKey: 'showBookingWageOnApp',
    field: 'isShowBookingWage',
    fieldType: 'toggle',
    keywords: ['wage', 'booking', 'app', 'pay']
  },
  {
    id: 'sched.scheduling.app.isHasCarOnApply',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'app',
    labelKey: 'isHasCarOnApply',
    field: 'isHasCarOnApply',
    fieldType: 'toggle',
    keywords: ['car', 'vehicle', 'apply']
  },
  {
    id: 'sched.scheduling.app.enableEmailNotificationsForBookings',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'app',
    labelKey: 'enableEmailNotificationsForBookings',
    field: 'enableEmailNotificationsForBookings',
    fieldType: 'toggle',
    keywords: ['email', 'notification', 'booking']
  },
  {
    id: 'sched.scheduling.app.showTeam',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'app',
    labelKey: 'showTeamMembersPerJobOnStaffApp',
    field: 'showTeam',
    fieldType: 'toggle',
    keywords: ['team', 'members', 'app']
  },
  {
    id: 'sched.scheduling.app.showTeamPhones',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'app',
    labelKey: 'showTeamMembersPhonesApp',
    field: 'showTeamPhones',
    fieldType: 'toggle',
    keywords: ['team', 'phone', 'contact']
  },
  {
    id: 'sched.scheduling.app.showTeamPhonesToCaptain',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'app',
    labelKey: 'showTeamPhonesToCaptain',
    field: 'showTeamPhonesToCaptain',
    fieldType: 'toggle',
    keywords: ['team', 'phone', 'captain']
  },
  {
    id: 'sched.scheduling.app.maskLocationAddressInAppOffers',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'app',
    labelKey: 'maskLocationAddressInAppOffers',
    field: 'maskLocationAddressInAppOffers',
    fieldType: 'toggle',
    keywords: ['location', 'address', 'gps', 'hide', 'mask', 'offers']
  },
  {
    id: 'sched.scheduling.app.hideAreaName',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'app',
    labelKey: 'hideAreaNameInAppOffers',
    field: 'showAreaName',
    fieldType: 'toggle',
    keywords: ['area', 'hide', 'mask', 'offers', 'location']
  },
  {
    id: 'sched.scheduling.app.hideAreaNameUntilClockIn',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'app',
    labelKey: 'hideAreaNameInAppOffersUntilClockIn',
    field: 'showAreaNameUntilClockIn',
    fieldType: 'toggle',
    keywords: ['area', 'hide', 'clock in', 'offers']
  },
  {
    id: 'sched.scheduling.app.maskClientInAppOffers',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'app',
    labelKey: 'maskClientInAppOffers',
    field: 'maskClientInAppOffers',
    fieldType: 'toggle',
    keywords: ['client', 'hide', 'mask', 'offers']
  },
  {
    id: 'sched.scheduling.app.maskGeneralCommentsInAppOffers',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'app',
    labelKey: 'maskGeneralCommentsInAppOffers',
    field: 'maskGeneralCommentsInAppOffers',
    fieldType: 'toggle',
    keywords: ['comments', 'hide', 'mask', 'offers']
  },
  {
    id: 'sched.scheduling.app.maskProjectFieldsAppOffers',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'app',
    labelKey: 'maskProjectFieldsAppOffers',
    field: 'maskProjectFieldsAppOffers',
    fieldType: 'toggle',
    keywords: ['project fields', 'hide', 'mask', 'offers']
  },
  {
    id: 'sched.scheduling.app.maskNotesInAppOffers',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'app',
    labelKey: 'maskNotesInAppOffers',
    field: 'maskNotesInAppOffers',
    fieldType: 'toggle',
    keywords: ['notes', 'hide', 'mask', 'offers']
  },
  {
    id: 'sched.scheduling.app.toLimitWorkFlowCompletionTime',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'app',
    labelKey: 'setWorkFlowCompletionHeader',
    field: 'toLimitWorkFlowCompletionTime',
    fieldType: 'toggle',
    keywords: ['workflow', 'completion', 'time']
  },
  {
    id: 'sched.scheduling.app.hoursToCompleteWorkFlowAfterShiftStarts',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'app',
    labelKey: 'hoursToCompleteWorkFlowAfterShiftStarts',
    searchLabelKey: 'settingsSearchCompleteWorkflowsAfterShiftStart',
    field: 'hoursToCompleteWorkFlowAfterShiftStarts',
    parentField: 'toLimitWorkFlowCompletionTime',
    keywords: ['workflow', 'completion', 'hours', 'pre workflow', 'post workflow']
  },
  // Scheduling › Confirmations
  {
    id: 'sched.scheduling.confirmations.defaultConfirmationMessage',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'confirmations',
    labelKey: 'defaultConfirmationMessageSetting',
    field: 'defaultConfirmationMessage',
    keywords: ['confirmation', 'default message', 'template', 'text']
  },
  {
    id: 'sched.scheduling.confirmations.toCreateConfirmationAutomatically',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'confirmations',
    labelKey: 'toCreateConfirmationAutomatically',
    field: 'toCreateConfirmationAutomatically',
    fieldType: 'toggle',
    keywords: ['confirmation', 'automatic']
  },
  // Scheduling › Reminders
  {
    id: 'sched.scheduling.reminders.toSendShiftRemindersAutomatically',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'reminders',
    labelKey: 'sendAutoShiftRemindersToEmployees',
    field: 'toSendShiftRemindersAutomatically',
    fieldType: 'toggle',
    keywords: ['reminder', 'shift', 'automatic']
  },
  {
    id: 'sched.scheduling.reminders.remindersToBeSent',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'scheduling',
    sectionKey: 'reminders',
    labelKey: 'remindersToBeSent',
    field: 'remindersToBeSent',
    parentField: 'toSendShiftRemindersAutomatically',
    keywords: ['automated reminders', 'reminders', 'reminder', 'shift', 'booked employees', 'automatic']
  },
  // Timesheet › Timesheet (general)
  {
    id: 'sched.timesheet.general.clockInBeforeShiftStart',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'timesheet',
    labelKey: 'canClockInBeforeShiftStarts',
    searchLabelKey: 'settingsSearchClockInBeforeShiftStart',
    field: 'clockInBeforeShiftStart',
    keywords: ['clock in', 'before', 'shift']
  },
  {
    id: 'sched.timesheet.general.clockOutAfterShiftEnd',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'timesheet',
    labelKey: 'canClockOutAfterShiftEnds',
    searchLabelKey: 'settingsSearchClockOutAfterShiftEnd',
    field: 'clockOutAfterShiftEnd',
    keywords: ['clock out', 'after', 'shift']
  },
  {
    id: 'sched.timesheet.general.clockInAfterShiftStart',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'timesheet',
    labelKey: 'canClockInAfterShiftStarts',
    searchLabelKey: 'settingsSearchClockInAfterShiftStart',
    field: 'clockInAfterShiftStart',
    keywords: ['clock in', 'after', 'shift']
  },
  {
    id: 'sched.timesheet.general.autoClockOutAfter',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'timesheet',
    labelKey: 'enableAutoClockOutAfterHours',
    field: 'autoClockOutAfter',
    keywords: ['clock out', 'automatic', 'hours']
  },
  {
    id: 'sched.timesheet.general.autoClockOutAfterHours',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'timesheet',
    labelKey: 'autoClockOutAfterHours',
    searchLabelKey: 'settingsSearchAutoClockOutAfterHours',
    field: 'autoClockOutAfterHours',
    parentField: 'autoClockOutAfter',
    keywords: ['clock out', 'automatic', 'hours', 'auto clock out']
  },
  {
    id: 'sched.timesheet.general.limitClockInTime',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'timesheet',
    labelKey: 'limitClockInTimeSetting',
    field: 'limitClockInTime',
    fieldType: 'toggle',
    keywords: ['clock in', 'clock out', 'attendance', 'limit']
  },
  {
    id: 'sched.timesheet.general.roundClockInAndOut',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'timesheet',
    labelKey: 'roundClockTimesNearestIncrement',
    field: 'roundClockInAndOut',
    fieldType: 'toggle',
    keywords: ['rounding', 'clock']
  },
  {
    id: 'sched.timesheet.general.toForceRestrictions',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'timesheet',
    labelKey: 'toForceRestrictions',
    field: 'toForceRestrictions',
    fieldType: 'toggle',
    keywords: ['restrictions', 'force']
  },
  {
    id: 'sched.timesheet.general.allowEventCaptainManualTimeStamp',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'timesheet',
    labelKey: 'eventCaptainSettingsManually',
    field: 'allowEventCaptainManualTimeStamp',
    fieldType: 'toggle',
    keywords: ['event captain', 'manual', 'timestamp']
  },
  {
    id: 'sched.timesheet.general.allowAreaCaptainEditClock',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'timesheet',
    labelKey: 'areaCaptainSettingsClock',
    field: 'allowAreaCaptainEditClock',
    fieldType: 'toggle',
    keywords: ['area captain', 'clock', 'edit']
  },
  {
    id: 'sched.timesheet.general.allowAreaCaptainManualTimeStamp',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'timesheet',
    labelKey: 'areaCaptainSettingsManually',
    field: 'allowAreaCaptainManualTimeStamp',
    fieldType: 'toggle',
    keywords: ['area captain', 'manual', 'timestamp']
  },
  {
    id: 'sched.timesheet.general.allowShiftCaptainEditClock',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'timesheet',
    labelKey: 'shiftCaptainSettingsClock',
    field: 'allowShiftCaptainEditClock',
    fieldType: 'toggle',
    keywords: ['shift captain', 'clock', 'edit']
  },
  {
    id: 'sched.timesheet.general.allowShiftCaptainManualTimeStamp',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'timesheet',
    labelKey: 'shiftCaptainSettingsManually',
    field: 'allowShiftCaptainManualTimeStamp',
    fieldType: 'toggle',
    keywords: ['shift captain', 'manual', 'timestamp']
  },
  // Timesheet › Timesheet app (mobile clock/attendance)
  {
    id: 'sched.timesheet.app.toAllowClockWithMobile',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'timesheetApp',
    labelKey: 'allowClockMobile',
    field: 'toAllowClockWithMobile',
    fieldType: 'toggle',
    keywords: ['clock', 'mobile', 'app', 'clock in', 'clock out']
  },
  {
    id: 'sched.timesheet.app.clockInMethod',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'timesheetApp',
    labelKey: 'clockInMethod',
    field: 'mobileClockMethods.clockIn.method',
    parentField: 'toAllowClockWithMobile',
    keywords: ['clock in', 'method', 'mobile', 'qr', 'gps']
  },
  {
    id: 'sched.timesheet.app.clockOutMethod',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'timesheetApp',
    labelKey: 'clockOutMethod',
    field: 'mobileClockMethods.clockOut.method',
    parentField: 'toAllowClockWithMobile',
    keywords: ['clock out', 'method', 'mobile', 'qr', 'gps']
  },
  {
    id: 'sched.timesheet.app.toRequireCheckInWithMobile',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'timesheetApp',
    labelKey: 'allowCheckInMobile',
    field: 'toRequireCheckInWithMobile',
    fieldType: 'toggle',
    parentField: 'toAllowClockWithMobile',
    keywords: ['check in', 'check out', 'mobile', 'area']
  },
  {
    id: 'sched.timesheet.app.checkInMethod',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'timesheetApp',
    labelKey: 'checkInMethod',
    field: 'mobileClockMethods.checkIn.method',
    parentField: 'toRequireCheckInWithMobile',
    keywords: ['check in', 'method', 'mobile']
  },
  {
    id: 'sched.timesheet.app.checkOutMethod',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'timesheetApp',
    labelKey: 'checkOutMethod',
    field: 'mobileClockMethods.checkOut.method',
    parentField: 'toRequireCheckInWithMobile',
    keywords: ['check out', 'method', 'mobile']
  },
  {
    id: 'sched.timesheet.app.autoClockOutAfterCheckOut',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'timesheetApp',
    labelKey: 'autoClockOutAfterCheckOutToggle',
    field: 'autoClockOutAfterCheckOut',
    parentField: 'toAllowClockWithMobile',
    keywords: ['auto clock out', 'check out', 'automatic']
  },
  {
    id: 'sched.timesheet.app.autoClockOutAfterCheckOutMinutes',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'timesheetApp',
    labelKey: 'autoClockOutAfterCheckOutDescription',
    field: 'autoClockOutAfterCheckOutDescription',
    parentField: 'autoClockOutAfterCheckOut',
    keywords: ['auto clock out', 'check out', 'minutes', 'number of minutes', 'automatic']
  },
  {
    id: 'sched.timesheet.app.toAllowGpsWhenClock',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'timesheetApp',
    labelKey: 'allowGEOClock',
    field: 'toAllowGpsWhenClock',
    fieldType: 'toggle',
    parentField: 'toAllowClockWithMobile',
    keywords: ['gps', 'geo fence', 'location', 'clock']
  },
  {
    id: 'sched.timesheet.app.distanceFromShiftAddress',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'timesheetApp',
    labelKey: 'distanceFromShiftAddress',
    searchLabelKey: 'settingsSearchDistanceFromShiftAddress',
    field: 'distanceFromShiftAddress',
    parentField: 'toAllowGpsWhenClock',
    keywords: ['gps', 'distance', 'radius', 'geo fence', 'shift address']
  },
  {
    id: 'sched.timesheet.app.toSendClockInReminder',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'timesheetApp',
    labelKey: 'toSendClockInReminder',
    field: 'toSendClockInReminder',
    fieldType: 'toggle',
    parentField: 'toAllowClockWithMobile',
    keywords: ['clock in', 'reminder', 'notification']
  },
  {
    id: 'sched.timesheet.app.toSendClockOutReminder',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'timesheetApp',
    labelKey: 'toSendClockOutReminder',
    field: 'toSendClockOutReminder',
    fieldType: 'toggle',
    parentField: 'toAllowClockWithMobile',
    keywords: ['clock out', 'reminder', 'notification']
  },
  {
    id: 'sched.timesheet.app.allowCaptainsReportTheirOwnTimesheet',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'timesheetApp',
    labelKey: 'allowCaptainsReportTheirOwnTimesheet',
    field: 'allowCaptainsReportTheirOwnTimesheet',
    fieldType: 'toggle',
    parentField: 'toAllowClockWithMobile',
    keywords: ['captain', 'timesheet', 'report']
  },
  {
    id: 'sched.timesheet.app.allowEditTimesheetViaApp',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'timesheetApp',
    labelKey: 'allowEditTimesheetViaApp',
    field: 'allowEditTimesheetViaApp',
    fieldType: 'toggle',
    parentField: 'toAllowClockWithMobile',
    requiresCrewTimesheetPage: true,
    keywords: ['edit timesheet', 'app', 'employee', 'crew']
  },
  // Timesheet › Breaks
  {
    id: 'sched.timesheet.breaks.displayBreakTimes',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'breaks',
    labelKey: 'displayBreakTimes',
    field: 'isBreakTimesDisplayed',
    fieldType: 'toggle',
    keywords: ['break', 'rest']
  },
  {
    id: 'sched.timesheet.breaks.toRequireClockBreaks',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'breaks',
    labelKey: 'requireEmployeesClockBreaks',
    field: 'toRequireClockBreaks',
    fieldType: 'toggle',
    parentField: 'isBreakTimesDisplayed',
    keywords: ['break', 'clock', 'require']
  },
  {
    id: 'sched.timesheet.breaks.isDefaultBreakTimes',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'breaks',
    labelKey: 'setDefaultBreakTimes',
    field: 'isDefaultBreakTimes',
    fieldType: 'toggle',
    parentField: 'isBreakTimesDisplayed',
    keywords: ['break', 'default']
  },
  // Timesheet › Work week
  {
    id: 'sched.timesheet.workWeek.defaultTypeForNewEmployees',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'workWeek',
    labelKey: 'defaultTypeForNewEmployees',
    field: 'isDaily',
    keywords: ['default type', 'new employees', 'daily', 'shift']
  },
  {
    id: 'sched.timesheet.workWeek.defaultWorkDayHours',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'workWeek',
    labelKey: 'defaultWorkDayHours',
    field: 'defaultJobStartTime',
    keywords: ['work day', 'hours', 'start time', 'end time', 'default']
  },
  {
    id: 'sched.timesheet.workWeek.selectWorkDays',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'workWeek',
    labelKey: 'selectWorkDays',
    field: 'workDays',
    keywords: ['work days', 'week', 'working days']
  },
  {
    id: 'sched.timesheet.workWeek.toSendJobClockInReminder',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'workWeek',
    labelKey: 'toSendJobClockInReminder',
    field: 'toSendJobClockInReminder',
    fieldType: 'toggle',
    parentField: 'isDaily',
    keywords: ['clock in', 'reminder', 'daily']
  },
  {
    id: 'sched.timesheet.workWeek.toSendJobClockOutReminder',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'workWeek',
    labelKey: 'toSendJobClockOutReminder',
    field: 'toSendJobClockOutReminder',
    fieldType: 'toggle',
    parentField: 'isDaily',
    keywords: ['clock out', 'reminder', 'daily']
  },
  // Timesheet › Weekly preferences
  {
    id: 'sched.timesheet.notification.isWeeklyPreferencesSubmissionsActive',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'weeklyPreferencesTimeRange',
    labelKey: 'defineWeeklyPreferencesSubmissionsTimeRanges',
    field: 'isWeeklyPreferencesSubmissionsActive',
    fieldType: 'toggle',
    keywords: ['weekly', 'preferences', 'submission']
  },
  {
    id: 'sched.timesheet.notification.defaultNotificationText',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'weeklyPreferencesTimeRange',
    labelKey: 'defaultNotificationText',
    field: 'defaultNotificationText',
    parentField: 'isWeeklyPreferencesSubmissionsActive',
    keywords: ['weekly', 'preferences', 'notification', 'default message', 'template']
  },
  {
    id: 'sched.timesheet.notification.fromDay',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'weeklyPreferencesTimeRange',
    labelKey: 'fromDay',
    field: 'fromDayNotificationDay',
    parentField: 'isWeeklyPreferencesSubmissionsActive',
    keywords: ['weekly', 'preferences', 'from day', 'time range', 'start']
  },
  {
    id: 'sched.timesheet.notification.untillDay',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'weeklyPreferencesTimeRange',
    labelKey: 'untillDay',
    field: 'untillDayNotificationDay',
    parentField: 'isWeeklyPreferencesSubmissionsActive',
    keywords: ['weekly', 'preferences', 'until day', 'time range', 'end']
  },
  // Timesheet › Availability Window
  {
    id: 'sched.timesheet.availabilityWindow.startDay',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'availabilityWindow',
    labelKey: 'availabilityWindowStartDay',
    field: 'availabilityWindowStartDay',
    parentField: 'isAvailabilityWindowActive',
    requiresAvailabilityWindow: true,
    keywords: ['availability', 'window', 'start day', 'submission', 'next month']
  },
  {
    id: 'sched.timesheet.availabilityWindow.endDay',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'availabilityWindow',
    labelKey: 'availabilityWindowEndDay',
    field: 'availabilityWindowEndDay',
    parentField: 'isAvailabilityWindowActive',
    requiresAvailabilityWindow: true,
    keywords: ['availability', 'window', 'end day', 'submission', 'next month']
  },
  // Timesheet › Absences
  {
    id: 'sched.timesheet.absences.yearlyDailyEmployeesTimeOff',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'absences',
    labelKey: 'yearlyAmountOfTime',
    field: 'yearlyDailyEmployeesTimeOff',
    keywords: ['absence', 'time off', 'yearly', 'leave']
  },
  // Timesheet › Timesheet approval
  {
    id: 'sched.timesheet.approval.requireClientTimesheetSignoff',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'timesheetApproval',
    labelKey: 'requireClientTimesheetSignoff',
    field: 'requireClientTimesheetSignoff',
    fieldType: 'toggle',
    keywords: ['timesheet', 'approval', 'client', 'signoff']
  },
  {
    id: 'sched.timesheet.approval.requireEmployeeTimesheetSignoff',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'timesheet',
    sectionKey: 'timesheetApproval',
    labelKey: 'requireEmployeeTimesheetSignoff',
    field: 'requireEmployeeTimesheetSignoff',
    fieldType: 'toggle',
    keywords: ['timesheet', 'approval', 'employee', 'signoff']
  },
  // Scheduling & Timesheet table sub-pages (page-level)
  {
    id: 'sched.uniforms',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'uniforms',
    labelKey: 'uniforms',
    pageLevel: true,
    keywords: ['dress code', 'clothing']
  },
  {
    id: 'sched.locations',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'locations',
    labelKey: 'locations',
    pageLevel: true,
    keywords: ['gps', 'geofence', 'address']
  },
  {
    id: 'sched.tags',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'tags',
    labelKey: 'tags',
    pageLevel: true,
    keywords: ['event tags', 'labels']
  },
  {
    id: 'sched.costCenters',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'costCenters',
    labelKey: 'costCenters',
    pageLevel: true,
    keywords: ['accounting', 'budget']
  },
  {
    id: 'sched.projectTemplates',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'projectTemplates',
    labelKey: 'projectTemplates',
    pageLevel: true,
    keywords: ['event templates']
  },
  {
    id: 'sched.unbookReasons',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'unbookReasons',
    route: accountRoute('unbook-reasons'),
    labelKey: 'unbookReasonsManagement',
    pageLevel: true,
    privilege: privileges.ACCOUNT_ADMIN,
    keywords: ['no show', 'no-show', 'unbook', 'cancellation', 'call off', 'reason']
  },
  {
    id: 'sched.weeklyPlannerTemplates',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'weeklyPlannerTemplates',
    labelKey: 'weeklyPlannerTemplates',
    pageLevel: true
  },
  {
    id: 'sched.projectFields',
    tabKey: 'scheduling-timesheet',
    subTabKey: 'projectFields',
    labelKey: 'projectFields',
    pageLevel: true,
    keywords: ['custom event fields']
  },

  // ── PAYROLL (global) ────────────────────────────────────────────────────────
  // Payroll › General
  {
    id: 'payroll.general.paymentPeriodType',
    tabKey: 'payroll',
    subTabKey: 'general',
    sectionKey: 'general',
    labelKey: 'paymentPeriodType',
    field: 'paymentPeriodType',
    fieldType: 'select',
    valueBase: 'payrollConfig',
    keywords: ['payment', 'period', 'cycle']
  },
  {
    id: 'payroll.general.paymentStartDate',
    tabKey: 'payroll',
    subTabKey: 'general',
    sectionKey: 'general',
    labelKey: 'startDatePayment',
    field: 'paymentStartDate',
    valueBase: 'payrollConfig',
    keywords: ['payment', 'start date']
  },
  {
    id: 'payroll.general.hourlyWage',
    tabKey: 'payroll',
    subTabKey: 'general',
    sectionKey: 'general',
    labelKey: 'hourlyWage',
    field: 'wage.hourlyWage',
    valueBase: 'payrollConfig',
    keywords: ['wage', 'hourly', 'rate', 'pay']
  },
  {
    id: 'payroll.general.minimumWage',
    tabKey: 'payroll',
    subTabKey: 'general',
    sectionKey: 'general',
    labelKey: 'minimumWage',
    field: 'minimumWage',
    valueBase: 'payrollConfig',
    keywords: ['minimum', 'wage', 'hourly', 'rate', 'compliance']
  },
  {
    id: 'payroll.general.flatWageIsActive',
    tabKey: 'payroll',
    subTabKey: 'general',
    sectionKey: 'general',
    labelKey: 'flatWageIsActive',
    field: 'flatIsActive',
    fieldType: 'toggle',
    valueBase: 'payrollConfig',
    // Gates every flat wage/hours control in the app (payroll, positions, supplier rates),
    // so keep it findable from those wordings — while it is off the gated rows are unindexed.
    keywords: ['flat', 'wage', 'salary', 'rate', 'hours', 'supplier', 'charge']
  },
  {
    id: 'payroll.general.flatWage',
    tabKey: 'payroll',
    subTabKey: 'general',
    sectionKey: 'general',
    labelKey: 'flatWage',
    field: 'wage.flatWage',
    valueBase: 'payrollConfig',
    parentField: 'flatIsActive',
    keywords: ['flat', 'wage', 'salary', 'pay']
  },
  {
    id: 'payroll.general.flatHours',
    tabKey: 'payroll',
    subTabKey: 'general',
    sectionKey: 'general',
    labelKey: 'flatHours',
    field: 'wage.flatHours',
    valueBase: 'payrollConfig',
    parentField: 'flatIsActive',
    keywords: ['flat', 'hours', 'salary']
  },
  // Payroll › Overtime
  {
    id: 'payroll.overtime.dailyOvertime',
    tabKey: 'payroll',
    subTabKey: 'general',
    sectionKey: 'overtime',
    labelKey: 'dailyOvertime',
    field: 'dailyOvertimeIsActive',
    fieldType: 'toggle',
    valueBase: 'payrollConfig',
    keywords: ['ot', 'overtime', 'extra hours']
  },
  {
    id: 'payroll.overtime.dailyOvertimeStart',
    tabKey: 'payroll',
    subTabKey: 'general',
    sectionKey: 'overtime',
    labelKey: 'dailyOvertimeStartsAfterHours',
    searchLabelKey: 'settingsSearchDailyOvertimeStart',
    field: 'dailyOvertimeOvertimeStart',
    valueBase: 'payrollConfig',
    parentField: 'dailyOvertimeIsActive',
    keywords: ['ot', 'overtime', 'daily', 'threshold', 'hours']
  },
  {
    id: 'payroll.overtime.dailyOvertimeRate',
    tabKey: 'payroll',
    subTabKey: 'general',
    sectionKey: 'overtime',
    labelKey: 'dailyOvertimeRatePerHour',
    searchLabelKey: 'settingsSearchDailyOvertimeRate',
    field: 'dailyOvertimeRate',
    valueBase: 'payrollConfig',
    parentField: 'dailyOvertimeIsActive',
    keywords: ['ot', 'overtime', 'daily', 'rate', 'multiplier']
  },
  {
    id: 'payroll.overtime.doubleDailyOvertimeStart',
    tabKey: 'payroll',
    subTabKey: 'general',
    sectionKey: 'overtime',
    labelKey: 'doubleDailyOvertimeStartsAfterHours',
    searchLabelKey: 'settingsSearchDoubleDailyOvertimeStart',
    field: 'doubleDailyOvertimeStart',
    valueBase: 'payrollConfig',
    parentField: 'dailyOvertimeIsActive',
    keywords: ['ot', 'double overtime', 'daily', 'threshold', 'hours']
  },
  {
    id: 'payroll.overtime.doubleDailyOvertimeRate',
    tabKey: 'payroll',
    subTabKey: 'general',
    sectionKey: 'overtime',
    labelKey: 'doubleDailyOvertimeRatePerHour',
    searchLabelKey: 'settingsSearchDoubleDailyOvertimeRate',
    field: 'doubleDailyOvertimeRate',
    valueBase: 'payrollConfig',
    parentField: 'dailyOvertimeIsActive',
    keywords: ['ot', 'double overtime', 'daily', 'rate', 'multiplier']
  },
  {
    id: 'payroll.overtime.weeklyOvertime',
    tabKey: 'payroll',
    subTabKey: 'general',
    sectionKey: 'overtime',
    labelKey: 'weeklyOvertime',
    field: 'weeklyIsActive',
    fieldType: 'toggle',
    valueBase: 'payrollConfig',
    keywords: ['ot', 'overtime']
  },
  {
    id: 'payroll.overtime.weeklyOvertimeStart',
    tabKey: 'payroll',
    subTabKey: 'general',
    sectionKey: 'overtime',
    labelKey: 'weeklyOvertimeStartsAfterHours',
    searchLabelKey: 'settingsSearchWeeklyOvertimeStart',
    field: 'weeklyOvertimeStart',
    valueBase: 'payrollConfig',
    parentField: 'weeklyIsActive',
    keywords: ['ot', 'overtime', 'weekly', 'threshold', 'hours']
  },
  {
    id: 'payroll.overtime.weeklyOvertimeRate',
    tabKey: 'payroll',
    subTabKey: 'general',
    sectionKey: 'overtime',
    labelKey: 'weeklyOvertimeRatePerHour',
    searchLabelKey: 'settingsSearchWeeklyOvertimeRate',
    field: 'weeklyOvertimeRate',
    valueBase: 'payrollConfig',
    parentField: 'weeklyIsActive',
    keywords: ['ot', 'overtime', 'weekly', 'rate', 'multiplier']
  },
  // Payroll › Paid breaks
  {
    id: 'payroll.general.deductBreaks',
    tabKey: 'payroll',
    subTabKey: 'general',
    sectionKey: 'paidBreaks',
    labelKey: 'deductBreakTimesFromPayrollCalculation',
    field: 'deductBreaktimesIsActive',
    fieldType: 'toggle',
    valueBase: 'payrollConfig',
    keywords: ['break', 'deduction']
  },
  // Payroll › Employee app
  {
    id: 'payroll.general.viewPayroll',
    tabKey: 'payroll',
    subTabKey: 'general',
    sectionKey: 'employeeApp',
    labelKey: 'allowEmployeesViewPayroll',
    field: 'isShowPayrollOnApp',
    fieldType: 'toggle',
    valueBase: 'payrollConfig',
    keywords: ['income', 'wage', 'app']
  },
  {
    id: 'payroll.general.payrollVisibilityMode',
    tabKey: 'payroll',
    subTabKey: 'general',
    sectionKey: 'employeeApp',
    labelKey: 'payrollVisibilityMode',
    field: 'payrollVisibilityMode',
    fieldType: 'select',
    valueBase: 'payrollConfig',
    parentField: 'isShowPayrollOnApp',
    keywords: ['income', 'tips', 'shared', 'fields', 'app']
  },
  // Payroll › Holiday pay
  {
    id: 'payroll.general.holidayPay',
    tabKey: 'payroll',
    subTabKey: 'general',
    sectionKey: 'holidayPay',
    labelKey: 'holidayPay',
    field: 'holidayIsActive',
    fieldType: 'toggle',
    valueBase: 'payrollConfig',
    keywords: ['holiday', 'bank holiday']
  },
  // Payroll › Night hours (shown for IL accounts only)
  {
    id: 'payroll.nightHours.nightHoursIsActive',
    tabKey: 'payroll',
    subTabKey: 'general',
    sectionKey: 'nightHours',
    labelKey: 'nightHoursIsActive',
    field: 'nightHoursIsActive',
    fieldType: 'toggle',
    valueBase: 'payrollConfig',
    countryCode: 'il',
    keywords: ['night', 'night hours', 'night shift']
  },
  // Payroll › Invoices (Ubeya admin only)
  {
    id: 'payroll.invoices.isInvoiceAutoIncrement',
    tabKey: 'payroll',
    subTabKey: 'general',
    sectionKey: 'invoices',
    labelKey: 'isActive',
    field: 'isInvoiceAutoIncrement',
    fieldType: 'toggle',
    valueBase: 'payrollConfig',
    requiresUbeyaAdmin: true,
    keywords: ['invoice', 'auto increment', 'numbering']
  },
  {
    id: 'payroll.salaryFields',
    tabKey: 'payroll',
    subTabKey: 'salary-fields',
    labelKey: 'salary-fields',
    pageLevel: true,
    keywords: ['custom salary']
  },

  // ── RULES ENGINE (global, CRUD) ─────────────────────────────────────────────
  {
    id: 'rules-engine.ruleSets',
    tabKey: 'rules-engine',
    labelKey: 'rules-engine',
    pageLevel: true,
    keywords: ['compliance', 'rules', 'labour law', 'max hours', 'rest', 'certifications']
  },

  // ── TIMESHEET / TASK FIELDS (global, CRUD) ─────────────────────────────────
  {
    id: 'timesheet-fields.fields',
    tabKey: 'timesheet-fields',
    subTabKey: 'fields',
    labelKey: 'fields',
    pageLevel: true,
    keywords: ['task fields', 'shift tasks']
  },

  // ── SUPPLIER SETTINGS (global) ──────────────────────────────────────────────
  // Supplier rates › Pay rates
  {
    id: 'supplier-settings.rates.payRates.hourlyWage',
    tabKey: 'supplier-settings',
    subTabKey: 'rates',
    sectionKey: 'suppliersSettingsRatesWage',
    labelKey: 'hourlyRate',
    field: 'supplierWage.hourlyWage',
    valueBase: 'payrollConfig',
    keywords: ['hourly rate', 'rate', 'hourly', 'pay', 'supplier', 'agency', 'vendor', 'wage']
  },
  {
    id: 'supplier-settings.rates.payRates.flatWage',
    tabKey: 'supplier-settings',
    subTabKey: 'rates',
    sectionKey: 'suppliersSettingsRatesWage',
    labelKey: 'flatWage',
    field: 'supplierWage.flatWage',
    valueBase: 'payrollConfig',
    requiresFlatWage: true,
    keywords: ['flat', 'rate', 'pay', 'supplier', 'agency', 'vendor', 'wage']
  },
  {
    id: 'supplier-settings.rates.payRates.flatHours',
    tabKey: 'supplier-settings',
    subTabKey: 'rates',
    sectionKey: 'suppliersSettingsRatesWage',
    labelKey: 'flatHours',
    field: 'supplierWage.flatHours',
    valueBase: 'payrollConfig',
    requiresFlatWage: true,
    keywords: ['flat', 'hours', 'rate', 'pay', 'supplier', 'agency', 'vendor']
  },
  // Supplier rates › Charge rates
  {
    id: 'supplier-settings.rates.chargeRates.hourlyWage',
    tabKey: 'supplier-settings',
    subTabKey: 'rates',
    sectionKey: 'suppliersSettingsRatesClientWage',
    labelKey: 'hourlyRate',
    field: 'supplierClientWage.hourlyWage',
    valueBase: 'payrollConfig',
    keywords: ['hourly rate', 'rate', 'hourly', 'pay', 'charge', 'client', 'supplier', 'agency', 'vendor']
  },
  {
    id: 'supplier-settings.rates.chargeRates.flatWage',
    tabKey: 'supplier-settings',
    subTabKey: 'rates',
    sectionKey: 'suppliersSettingsRatesClientWage',
    labelKey: 'flatWage',
    field: 'supplierClientWage.flatWage',
    valueBase: 'payrollConfig',
    requiresFlatWage: true,
    keywords: ['flat', 'rate', 'charge', 'client', 'supplier', 'agency', 'vendor']
  },
  {
    id: 'supplier-settings.rates.chargeRates.flatHours',
    tabKey: 'supplier-settings',
    subTabKey: 'rates',
    sectionKey: 'suppliersSettingsRatesClientWage',
    labelKey: 'flatHours',
    field: 'supplierClientWage.flatHours',
    valueBase: 'payrollConfig',
    requiresFlatWage: true,
    keywords: ['flat', 'hours', 'rate', 'charge', 'client', 'supplier', 'agency', 'vendor']
  },

  // ── CLIENT SETTINGS (global) ────────────────────────────────────────────────
  {
    id: 'client-settings.general.allowClientToBlockEmployees',
    tabKey: 'client-settings',
    subTabKey: 'general',
    sectionKey: 'general',
    labelKey: 'allowClientToBlockEmployees',
    field: 'allowClientsToBlockEmployees',
    fieldType: 'toggle',
    valueBase: 'crmConfig',
    keywords: ['client', 'block']
  },
  {
    id: 'client-settings.general.showOriginalSupplierToClient',
    tabKey: 'client-settings',
    subTabKey: 'general',
    sectionKey: 'general',
    labelKey: 'showOriginalSupplierToClient',
    field: 'showOriginalSupplierToClient',
    fieldType: 'toggle',
    valueBase: 'crmConfig',
    keywords: ['supplier', 'client', 'privacy']
  },

  // ── COMPANY PROFILE (global, single page composite) ─────────────────────────
  {
    id: 'company-profile',
    tabKey: 'company-profile',
    labelKey: 'company-profile',
    pageLevel: true,
    keywords: ['logo', 'branding', 'about us', 'links', 'contacts']
  },

  // ── ADMIN MANAGEMENT (global, CRUD) ─────────────────────────────────────────
  {
    id: 'admin-management.admins',
    tabKey: 'admin-management',
    subTabKey: 'admins',
    labelKey: 'admins',
    pageLevel: true,
    keywords: ['users', 'managers', 'security']
  },
  {
    id: 'admin-management.roles',
    tabKey: 'admin-management',
    subTabKey: 'roles',
    labelKey: 'roles',
    pageLevel: true,
    keywords: ['permissions', 'privileges']
  },

  // ── KIOSK (global, CRUD) ────────────────────────────────────────────────────
  {
    id: 'kiosk.devices',
    tabKey: 'kiosk',
    subTabKey: 'devices',
    labelKey: 'kiosk',
    pageLevel: true,
    keywords: ['tablet', 'clock station', 'qr']
  },

  // ── HELP DESK (per-branch, CRUD) ────────────────────────────────────────────
  {
    id: 'help-desk',
    tabKey: 'help-desk',
    labelKey: 'help-desk',
    pageLevel: true,
    keywords: ['support', 'tickets', 'categories']
  },
  {
    id: 'help-desk.visibleToEmployees',
    tabKey: 'help-desk',
    route: accountRoute('help-desk', 'settings'),
    labelKey: 'allowUsersToViewHelpDeskOnApp',
    field: 'isVisibleToEmployees',
    fieldType: 'toggle',
    keywords: ['visibility', 'employee access', 'show help desk', 'crew app', 'mobile']
  },
  {
    id: 'help-desk.resolution.confirmationMessage',
    tabKey: 'help-desk',
    route: accountRoute('help-desk', 'settings'),
    sectionKey: 'resolution',
    labelKey: 'help_desk_resolution_confirmation_message',
    field: 'resolutionConfirmationMessage',
    keywords: ['resolution message', 'confirmation message', 'resolved message', 'auto close message']
  },
  {
    id: 'help-desk.resolution.timeoutHours',
    tabKey: 'help-desk',
    route: accountRoute('help-desk', 'settings'),
    sectionKey: 'resolution',
    labelKey: 'help_desk_resolution_timeout_label',
    field: 'resolutionTimeoutHours',
    fieldType: 'number',
    keywords: ['auto close', 'timeout', 'resolution timeout', 'close timer', 'hours']
  },
  {
    id: 'help-desk.resolution.confirmYesMessage',
    tabKey: 'help-desk',
    route: accountRoute('help-desk', 'settings'),
    sectionKey: 'resolution',
    labelKey: 'help_desk_resolution_yes_button_label',
    field: 'resolutionConfirmYesMessage',
    keywords: ['yes button', 'confirm yes', 'resolution yes', 'button label']
  },
  {
    id: 'help-desk.resolution.confirmNoMessage',
    tabKey: 'help-desk',
    route: accountRoute('help-desk', 'settings'),
    sectionKey: 'resolution',
    labelKey: 'help_desk_resolution_no_button_label',
    field: 'resolutionConfirmNoMessage',
    keywords: ['no button', 'confirm no', 'resolution no', 'button label']
  }
];

export const SETTINGS_SEARCH_INDEX = raw.map((entry) => {
  const tab = SETTINGS_TABS[entry.tabKey];
  return {
    ...entry,
    scope: entry.scope || tab.scope,
    isBranch: tab.isBranch,
    route:
      entry.route ||
      (tab.isBranch ? branchRoute(entry.tabKey, entry.subTabKey) : accountRoute(entry.tabKey, entry.subTabKey))
  };
});

export const POPULAR_SETTING_IDS = [
  'sched.scheduling.app.maskLocationAddressInAppOffers',
  'sched.timesheet.general.limitClockInTime',
  'payroll.overtime.dailyOvertime',
  'sched.scheduling.general.enableOpeningEventChatGroups',
  'general.other.photoVerification',
  'admin-management.roles'
];
